import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";

/* ---------- shared clock for relative times ---------- */
const nowListeners = new Set<() => void>();
let nowValue = Date.now();
let nowTimer: ReturnType<typeof setInterval> | undefined;

function subscribeNow(listener: () => void): () => void {
  nowListeners.add(listener);
  if (!nowTimer) {
    nowTimer = setInterval(() => {
      nowValue = Date.now();
      for (const l of nowListeners) l();
    }, 30_000);
  }
  return () => {
    nowListeners.delete(listener);
    if (nowListeners.size === 0 && nowTimer) {
      clearInterval(nowTimer);
      nowTimer = undefined;
    }
  };
}

/** Current time, refreshed every 30 s (one shared timer for all subscribers). */
export function useNow(): number {
  return useSyncExternalStore(subscribeNow, () => nowValue, () => nowValue);
}

export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const listener = (): void => setReduced(mq.matches);
    mq.addEventListener("change", listener);
    return () => mq.removeEventListener("change", listener);
  }, []);
  return reduced;
}

/** useState persisted in localStorage (per-viewer convenience only; tolerant to blocked storage). */
export function useStoredState<T>(key: string | undefined, initial: T): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => {
    if (!key) return initial;
    try {
      const raw = window.localStorage.getItem(key);
      return raw === null ? initial : (JSON.parse(raw) as T);
    } catch {
      return initial;
    }
  });
  const update = useCallback(
    (next: T) => {
      setValue(next);
      if (!key) return;
      try {
        window.localStorage.setItem(key, JSON.stringify(next));
      } catch {
        // ignore: storage unavailable
      }
    },
    [key],
  );
  return [value, update];
}

/** Returns true while `value` is truthy and for `ms` after it turns false (for exit animations). */
export function useDelayedUnmount(open: boolean, ms: number): boolean {
  const [mounted, setMounted] = useState(open);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => {
    clearTimeout(timer.current);
    if (open) setMounted(true);
    else timer.current = setTimeout(() => setMounted(false), ms);
    return () => clearTimeout(timer.current);
  }, [open, ms]);
  return mounted;
}
